import { CheckoutSdkFrameOptions, CheckoutSdkOptions, CheckoutSdkType } from '@mangopay/checkout-sdk-react-elements';
import {
  Subscription,
  AuthorizePostMessagePayload,
  BrowserInfo,
  CheckoutSdkHostEventMap,
  CheckoutSdkHostEventType,
  BaseSdkEventsDispatcher,
  CheckoutSdkFrameEventMap,
  CheckoutSdkFrameEventType,
  CheckoutSdkProxyMessagesOf,
  CheckoutSdkFrameParam,
  PaymentMethodSelectEventDetails,
  PaymentCompleteEventDetails,
  TokenizationCompleteEventDetails,
  CreatePaypalPaymentEventDetails,
  CreateCardPaymentEventDetails,
  ErrorEventDetails,
  CheckoutChangeEventDetails,
  DebuggerLogType,
  AuthorizePaymentInput,
  AuthorizeApplePayResult,
  CreateCardDirectPayInResult,
  CreateCardPayment,
  CreateApplePayPayIn,
  CreateApplePayPaymentData,
  CreateCardRegistration,
  CreateCardRegistrationPostMessagePayload,
  CreateGooglePayPayment,
  CreateGooglePayPaymentData,
  CreatePaypalPayment,
  DeactivateSavedCard,
  DeactivateSavedCardPostMessagePayload,
} from '@mangopay/checkout-sdk-core';
import { AppEventsDispatcherEventType } from './app-events-dispatcher-event-type';
import { AppEventsDispatcherEventMap } from './app-events-dispatcher-event-map';
import { env } from '../env';
import { CheckoutSdkInstance } from '../checkout-sdk-instance';
import { PaymentMethodsService, RedirectFlowService, StoredCallbacks } from '../services';
import { createFrameElement, deepClone, getBrowserInfo } from '../utils';

export class AppEventsDispatcher
  extends BaseSdkEventsDispatcher<CheckoutSdkHostEventMap<CheckoutSdkFrameOptions>, CheckoutSdkFrameEventMap>
  implements CheckoutSdkInstance
{
  static readonly version = env.version;

  private element: Element | null = null;

  private frameElement: HTMLIFrameElement | undefined;

  private _isDisabled = false;

  private _isComplete = false;

  private isMounted = false;

  private options!: CheckoutSdkOptions;

  private hostedCheckoutSdkUrl: string;

  private origin: string;

  private sdkType: CheckoutSdkType;

  private checkoutSdkSubscriptions: Subscription<AppEventsDispatcherEventType>[] = [];

  private unsubscribeCallbacks: (() => void)[] = [];

  private paymentMethodsService!: PaymentMethodsService;

  private redirectFlowService!: RedirectFlowService;

  get isComplete(): boolean {
    if (!this.isMounted) {
      return false;
    }

    return this._isComplete;
  }

  get isDisabled(): boolean {
    if (!this.isMounted) {
      return true;
    }

    return this._isDisabled;
  }

  constructor(options: CheckoutSdkOptions, hostedCheckoutSdkUrl: string, origin: string, sdkType: CheckoutSdkType) {
    super();
    this.hostedCheckoutSdkUrl = hostedCheckoutSdkUrl;
    this.origin = origin;
    this.sdkType = sdkType;

    this.initializeServices(options);
  }

  private initializeServices(options: CheckoutSdkOptions) {
    this.options = deepClone(options);
    this.initPaymentMethodsService();
    this.initRedirectFlowService();
  }

  private initPaymentMethodsService() {
    const paymentMethods = this.options?.paymentMethods;
    this.paymentMethodsService = new PaymentMethodsService(this.dispatchMessage.bind(this), paymentMethods);
  }

  private initRedirectFlowService() {
    this.redirectFlowService = new RedirectFlowService(this.dispatchSdkEventOutside.bind(this));
  }

  mount(elementOrSelector: HTMLElement | string): void {
    if (this.isMounted) {
      throw new Error('Element already mounted.');
    }
    this.element =
      typeof elementOrSelector === 'string' ? document.querySelector(elementOrSelector) : elementOrSelector;

    if (!this.element) {
      throw new Error(`Element is not found.`);
    }

    const frameUrl = new URL(this.hostedCheckoutSdkUrl);
    frameUrl.searchParams.append(CheckoutSdkFrameParam.Referrer, this.origin);
    frameUrl.searchParams.append(CheckoutSdkFrameParam.Version, env.version);
    frameUrl.searchParams.append(CheckoutSdkFrameParam.SdkType, this.sdkType);
    this.targetOrigin = frameUrl.origin;

    this.frameElement = createFrameElement(frameUrl.toString());
    this.frameElement.addEventListener('load', () => this.onFrameLoad());

    this.element.appendChild(this.frameElement);
  }

  umount(): void {
    console.warn('umount method is deprecated, please use unmount instead');
    this.unmount();
  }

  unmount(): void {
    this.frameElement?.remove();
    this.frameElement = undefined;
    this.target = undefined;

    this.unsubscribeCallbacks.forEach((unsubscribe) => unsubscribe());
    this.unsubscribeCallbacks = [];

    this.checkoutSdkSubscriptions = [];

    this.isMounted = false;
  }

  on<T extends keyof AppEventsDispatcherEventMap>(
    type: T,
    callback: (event: AppEventsDispatcherEventMap[T]) => void
  ): () => void {
    const listener = (event: Event) => callback(event as AppEventsDispatcherEventMap[T]);

    this.checkoutSdkSubscriptions.push({
      type,
      listener,
    });

    return () => {
      this.checkoutSdkSubscriptions = this.checkoutSdkSubscriptions.filter(
        (subscription) => subscription.listener !== listener
      );
    };
  }

  disable(): void {
    if (!this.isMounted) {
      return;
    }

    this._isDisabled = true;
    this.dispatchMessage(CheckoutSdkHostEventType.Disable, undefined);
  }

  enable(): void {
    if (!this.isMounted) {
      return;
    }

    this._isDisabled = false;
    this.dispatchMessage(CheckoutSdkHostEventType.Enable, undefined);
  }

  getBrowserInfo(): BrowserInfo {
    return getBrowserInfo();
  }

  completePayment(): void {
    this.dispatchMessage(CheckoutSdkHostEventType.CompletePayment, undefined);
  }

  setErrorMessage(errorMessage?: string): void {
    this.dispatchMessage(CheckoutSdkHostEventType.SetErrorMessage, errorMessage);
  }

  setLoading(loading: boolean): void {
    this.dispatchMessage(CheckoutSdkHostEventType.SetLoading, loading);
  }

  updateOptions(options: CheckoutSdkOptions): void {
    this.initializeServices(options);
    this.dispatchInitialize();
  }

  protected onIncomingMessage(message: CheckoutSdkProxyMessagesOf<CheckoutSdkFrameEventMap>): void {
    switch (message.eventType) {
      case CheckoutSdkFrameEventType.Initialize:
        this.onInitialized();
        break;
      case CheckoutSdkFrameEventType.Change:
        this.onChange(message.data);
        break;
      case CheckoutSdkFrameEventType.Cancel:
        this.dispatchMessage(CheckoutSdkHostEventType.LogCancelPayment, undefined);
        this.onCancel();
        break;
      case CheckoutSdkFrameEventType.Error:
        this.onError(message.data);
        break;
      case CheckoutSdkFrameEventType.Resize:
        this.onResize(message.data);
        break;
      case CheckoutSdkFrameEventType.OpenHostAuthPopupWindow:
        this.redirectFlowService.onOpenHostAuthPopupWindow(message.data, this.unsubscribeCallbacks);
        break;
      case CheckoutSdkFrameEventType.CloseHostAuthPopupWindow:
        this.onCloseHostAuthPopupWindow(message.data);
        break;
      case CheckoutSdkFrameEventType.OpenHostAuthPopupModal:
        this.redirectFlowService.onOpenHostAuthPopupModal(
          message.data,
          this.unsubscribeCallbacks,
          this.setLoading.bind(this)
        );
        break;
      case CheckoutSdkFrameEventType.CloseHostAuthPopupModal:
        this.onCloseHostAuthPopupModal(message.data);
        break;
      case CheckoutSdkFrameEventType.PaymentMethodSelect:
        this.onPaymentMethodSelect(message.data);
        break;
      case CheckoutSdkFrameEventType.PaymentComplete:
        this.onPaymentComplete(message.data);
        break;
      case CheckoutSdkFrameEventType.TokenizationComplete:
        this.onTokenizationComplete(message.data);
        break;
      case CheckoutSdkFrameEventType.CreateCardRegistration:
        this.handleCreateCardRegistration(message.data).catch(console.error);
        break;
      case CheckoutSdkFrameEventType.DeactivateSavedCard:
        this.handleDeactivateSavedCard(message.data).catch(console.error);
        break;
      case CheckoutSdkFrameEventType.CreatePaypalPayment:
        this.handleCreatePaypalPayment(message.data).catch(console.error);
        break;
      case CheckoutSdkFrameEventType.CreateGooglePayPayment:
        this.handleCreateGooglePayPayment(message.data).catch(console.error);
        break;
      case CheckoutSdkFrameEventType.CreateApplePayPaymentRequest:
        this.onApplePayPaymentRequest(message.data).catch(console.error);
        break;
      case CheckoutSdkFrameEventType.CreateApplePayPayIn:
        this.onApplePayCreatePayIn(message.data).catch(console.error);
        break;
      case CheckoutSdkFrameEventType.ShowPopupWindowOverlay:
        this.redirectFlowService.showPopupWindowOverlay(this.unsubscribeCallbacks);
        break;
      case CheckoutSdkFrameEventType.CreateCardPayment:
        this.handleCreateCardPayment(message.data).catch(console.error);
        break;

      default:
        console.warn(`Unknown event type: "${message.eventType}".`);
    }
  }

  private dispatchInitialize(): void {
    const paymentMethods = this.paymentMethodsService.getPaymentMethods();
    const fnNamesToFind: Array<keyof StoredCallbacks> = [
      'onCreateCardPayment',
      'onCreateGooglePayPayment',
      'onCreateApplePayPayIn',
    ];
    const [handleCardPayment, handleGooglePayPayment, handleApplePayPayment] = fnNamesToFind.map(
      (fn) => typeof this.paymentMethodsService.getCallbackFn(fn) === 'function'
    );

    const options: CheckoutSdkFrameOptions = {
      ...this.options,
      handleCardPayment,
      handleGooglePayPayment,
      handleApplePayPayment,
      paymentMethods,
    };
    // Workaround for Safari
    setTimeout(() => {
      this.dispatchMessage(CheckoutSdkHostEventType.Initialize, options);
    }, 10);
  }

  private onFrameLoad(): void {
    if (!this.frameElement?.contentWindow) {
      throw new Error('Failed to load MangopayCheckout SDK iFrame.');
    }

    this.target = this.frameElement.contentWindow;

    const unsubscribe = this.listen(window, this.targetOrigin as string);
    this.unsubscribeCallbacks.push(unsubscribe);

    this.dispatchInitialize();
  }

  private onInitialized(): void {
    if (!this.frameElement) {
      return;
    }

    this.isMounted = true;

    this.dispatchSdkEventOutside('initialize', undefined);
  }

  private onError(data: ErrorEventDetails): void {
    this.dispatchSdkEventOutside('error', data);
  }

  private onChange(data: CheckoutChangeEventDetails): void {
    this._isComplete = data.isComplete;

    this.dispatchSdkEventOutside('change', data);
  }

  private onCancel(): void {
    this.dispatchSdkEventOutside('cancel', undefined);
  }

  private onPaymentComplete(data: PaymentCompleteEventDetails): void {
    this.dispatchSdkEventOutside('paymentComplete', data);
  }

  private onTokenizationComplete(data: TokenizationCompleteEventDetails): void {
    this.dispatchSdkEventOutside('tokenizationComplete', data);
  }

  private handleDelegateCallbackError(type: DebuggerLogType, e: unknown) {
    this.onError({ error: { ResultMessage: (e as Error).message } });
    this.setLoading(false);
    this.dispatchMessage(CheckoutSdkHostEventType.LogHostError, {
      type,
      data: e,
    });
  }

  private onResize(size: DOMRectReadOnly): void {
    if (this.isMounted && this.frameElement) {
      this.frameElement.style.setProperty('height', `${size.height + 14}px`, 'important');
    }
  }

  private async onApplePayPaymentRequest(paymentData: AuthorizePaymentInput): Promise<void> {
    try {
      const authorizeResult = await this.paymentMethodsService.authorizeApplePayPaymentRequest(paymentData);
      console.log(authorizeResult, 'authorizerezult');
      this.onAuthorizeApplePayComplete(authorizeResult);
    } catch (e) {
      console.log(e, 'error');
    }
  }

  private async onApplePayCreatePayIn(paymentData: CreateApplePayPaymentData): Promise<void> {
    const onCreateApplePayPayIn = this.paymentMethodsService.getCallbackFn(
      'onCreateApplePayPayIn'
    ) as CreateApplePayPayIn;
    if (onCreateApplePayPayIn) {
      try {
        const result = await onCreateApplePayPayIn(paymentData);
        if (result) {
          this.dispatchMessage(CheckoutSdkHostEventType.CreateApplePayPaymentComplete, result);
        }
      } catch (e) {
        this.handleDelegateCallbackError('CREATE_GOOGLE_PAY_PAY_IN_REQUEST_ERROR', e);
      }
    }
  }

  private onAuthorizeApplePayComplete(data: AuthorizeApplePayResult) {
    this.dispatchMessage(CheckoutSdkHostEventType.AuthorizeApplePayComplete, data);
  }

  private onPaymentMethodSelect(data: PaymentMethodSelectEventDetails): void {
    this.dispatchSdkEventOutside('paymentMethodSelect', data);
  }

  private onHostAuthComplete(data: AuthorizePostMessagePayload): void {
    this.dispatchMessage(CheckoutSdkHostEventType.HostAuthComplete, data);
  }

  private onCloseHostAuthPopupWindow(data: AuthorizePostMessagePayload): void {
    this.redirectFlowService.hidePopupWindowOverlay();
    this.setLoading(false);
    this.onHostAuthComplete(data);
  }

  private onCloseHostAuthPopupModal(data: AuthorizePostMessagePayload): void {
    this.redirectFlowService.hidePopupModalOverlay(this.setLoading.bind(this));
    this.setLoading(false);
    this.onHostAuthComplete(data);
  }

  private async handleCreateCardRegistration({ cardType }: CreateCardRegistrationPostMessagePayload): Promise<void> {
    const onCreateCardRegistration = this.paymentMethodsService.getCallbackFn(
      'onCreateCardRegistration'
    ) as CreateCardRegistration;
    if (onCreateCardRegistration) {
      try {
        const result = await onCreateCardRegistration(cardType);
        if (result) {
          this.dispatchMessage(CheckoutSdkHostEventType.CreateCardRegistrationComplete, result);
        }
      } catch (e) {
        this.handleDelegateCallbackError('CREATE_REGISTRATION_REQUEST_ERROR', e);
      }
    }
  }

  private async handleDeactivateSavedCard(cardId: DeactivateSavedCardPostMessagePayload): Promise<void> {
    const onDeactivateSavedCard = this.paymentMethodsService.getCallbackFn(
      'onDeactivateSavedCard'
    ) as DeactivateSavedCard;

    if (onDeactivateSavedCard) {
      try {
        const result = await onDeactivateSavedCard(cardId);

        if (result) {
          this.dispatchMessage(CheckoutSdkHostEventType.DeactivateSavedCardComplete, result);
        }
      } catch (e) {
        this.handleDelegateCallbackError('DEACTIVATE_SAVED_CARD_ERROR', e);
      }
    }
  }

  private async handleCreateGooglePayPayment(paymentData: CreateGooglePayPaymentData): Promise<void> {
    const onCreateGooglePayment = this.paymentMethodsService.getCallbackFn(
      'onCreateGooglePayPayment'
    ) as CreateGooglePayPayment;
    if (onCreateGooglePayment) {
      try {
        const result = await onCreateGooglePayment(paymentData);
        if (result) {
          this.dispatchMessage(CheckoutSdkHostEventType.CreateGooglePayPaymentComplete, result);
        }
      } catch (e) {
        this.handleDelegateCallbackError('CREATE_GOOGLE_PAY_PAY_IN_REQUEST_ERROR', e);
      }
    }
  }

  private async handleCreatePaypalPayment({
    profilingAttemptReference,
    enableOneClick,
  }: CreatePaypalPaymentEventDetails): Promise<void> {
    const onCreatePaypalPayment = this.paymentMethodsService.getCallbackFn(
      'onCreatePaypalPayment'
    ) as CreatePaypalPayment;
    if (onCreatePaypalPayment) {
      try {
        const result = await onCreatePaypalPayment(profilingAttemptReference, enableOneClick);
        if (result) {
          this.dispatchMessage(CheckoutSdkHostEventType.CreatePaypalPaymentComplete, result);
        }
      } catch (e) {
        this.handleDelegateCallbackError('CREATE_GOOGLE_PAY_PAY_IN_REQUEST_ERROR', e);
      }
    }
  }

  private async handleCreateCardPayment(data: CreateCardPaymentEventDetails): Promise<void> {
    const onCreateCardPayment = this.paymentMethodsService.getCallbackFn('onCreateCardPayment') as CreateCardPayment;
    if (onCreateCardPayment) {
      try {
        const result: CreateCardDirectPayInResult = await onCreateCardPayment(data);
        if (result) {
          this.dispatchMessage(CheckoutSdkHostEventType.CreateCardPaymentComplete, result);
        }
      } catch (e) {
        this.handleDelegateCallbackError('CREATE_CARD_PAY_IN_REQUEST_ERROR', e);
      }
    }
  }

  private dispatchSdkEventOutside<
    TEventType extends keyof AppEventsDispatcherEventMap,
    TEventData extends AppEventsDispatcherEventMap[TEventType]['detail']
  >(eventType: TEventType, data: TEventData): void {
    this.checkoutSdkSubscriptions
      .filter((subscription) => subscription.type === eventType)
      .forEach((subscription) => subscription.listener(new CustomEvent<TEventData>(eventType, { detail: data })));
  }
}
